import React, { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { NoDataView } from "../NoDataView";
import { Button } from "@/components/atoms";

export interface ISocialImpactPost {
    id: string;
    caption: string;
    mediaUrl?: string;
    postedAt?: string;
    linkUrl?: string;
    reach?: number;
}

export interface SocialImpactPostsData {
    posts: ISocialImpactPost[];
}

const PAGE_SIZE = 4;

const formatPostedAt = (postedAt?: string): string | null => {
    if (!postedAt) return null;
    const d = new Date(postedAt);
    if (Number.isNaN(d.getTime())) return postedAt;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

export function SocialImpactPosts({ data }: { data: SocialImpactPostsData }) {
    const [displayedCount, setDisplayedCount] = useState(PAGE_SIZE);

    const sorted = useMemo(() =>
        (data?.posts ?? []).slice().sort((a, b) => {
            const aTime = a.postedAt ? new Date(a.postedAt).getTime() : 0;
            const bTime = b.postedAt ? new Date(b.postedAt).getTime() : 0;
            return aTime - bTime;
        }),
        [data]
    );

    const posts = sorted.slice(0, displayedCount);
    const isFullyLoaded = displayedCount >= sorted.length;

    if (!sorted.length) {
        return <NoDataView />;
    }

    return (
        <section className="w-full bg-white rounded-lg p-6">
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 items-stretch">
                {posts.map((post) => {
                    const postedAt = formatPostedAt(post.postedAt);
                    return (
                        <div key={post.id} className="w-full rounded-lg border border-gray-200 bg-white overflow-hidden flex flex-col">
                            {post.mediaUrl ? (
                                <div className="h-48 bg-gray-100">
                                    <img src={post.mediaUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                                </div>
                            ) : null}
                            <div className="p-4 space-y-2 flex flex-col flex-1">
                                {postedAt ? <p className="text-xs text-gray-500">{postedAt}</p> : null}
                                <p className="text-sm text-gray-800 whitespace-pre-wrap flex-1">{post.caption}</p>
                                {post.linkUrl ? (
                                    <div className="pt-1">
                                        <a
                                            href={post.linkUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-2 text-sm font-medium text-[#079a95] hover:underline focus:outline-none focus:ring-2 focus:ring-[#079a95] rounded"
                                        >
                                            <span>View Post</span>
                                            <ArrowUpRight className="h-4 w-4 shrink-0" />
                                        </a>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>
            {sorted.length > PAGE_SIZE && (
                <div className="relative flex items-center justify-center mt-4 pt-4">
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            className="px-6 py-2.5"
                            onClick={() => setDisplayedCount((prev) => Math.min(prev + PAGE_SIZE, sorted.length))}
                            disabled={isFullyLoaded}
                        >
                            Load More
                        </Button>
                        {displayedCount > PAGE_SIZE && (
                            <Button
                                variant="outline"
                                className="px-6 py-2.5"
                                onClick={() => setDisplayedCount(PAGE_SIZE)}
                            >
                                Collapse to Default
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
